import { useCallback, useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Text,
  Flex,
  Divider,
  useColorModeValue,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Button,
  Center,
  Spinner,
} from '@chakra-ui/react';

import { courseActions } from 'store/modules/course/course-slice';

import { SearchIcon, RepeatIcon, AddIcon } from '@chakra-ui/icons';
import CreateCourseMd from 'components/course/modals/CreateCourseMd';
import debounce from 'lodash.debounce';
import { fetchCoursesAction } from 'store/modules/course/course-actions';
import DataTable from 'react-data-table-component';
import courseColumns from 'utils/columns/courseColumns';
import { ReducerStatus } from 'utils/constants';

function CourseListPage() {
  document.title = 'Skill Quest | Admin - Courses';

  const dispatch = useDispatch();

  const courses = useSelector((state) => state.course.courses);
  const status = useSelector((state) => state.course.status);
  const metaData = useSelector((state) => state.course.metaData);

  const [perPage, setPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('asc');
  const [sortBy, setSortBy] = useState('');
  const [searchText, setSearchText] = useState('');

  const fetchData = useCallback(
    ({
      selectedPage,
      selectedPageSize,
      selectedOrderBy,
      selectedSortBy,
      selectedQuery,
    }) => {
      dispatch(
        fetchCoursesAction({
          currentPage: selectedPage || 1,
          pageSize: selectedPageSize || 10,
          orderBy: selectedOrderBy,
          sortBy: selectedSortBy,
          query: selectedQuery,
        })
      );
    },
    [dispatch]
  );

  const handlePerRowsChange = (newPerPage, page) => {
    if (newPerPage === perPage) return;
    setPerPage(newPerPage);
    fetchData({
      selectedPage: page,
      selectedPageSize: newPerPage,
      selectedOrderBy: orderBy,
      selectedSortBy: sortBy,
      selectedQuery: searchText,
    });
  };

  const handleSort = (column, sortDirection) => {
    if (!column?.sortField && sortDirection === orderBy) return;
    setOrderBy(sortDirection);
    setSortBy(column.sortField);
    fetchData({
      selectedPage: 1,
      selectedPageSize: perPage,
      selectedOrderBy: sortDirection,
      selectedSortBy: column.sortField,
      selectedQuery: searchText,
    });
  };

  const handlePageChange = (page) => {
    fetchData({
      selectedPage: page,
      selectedPageSize: perPage,
      selectedOrderBy: orderBy,
      selectedSortBy: sortBy,
      selectedQuery: searchText,
    });
  };

  const debouncedFetchData = useMemo(
    () =>
      debounce((query) => {
        fetchData({
          selectedPage: 1,
          selectedPageSize: perPage,
          selectedQuery: query,
        });
      }, 350),
    [fetchData, perPage]
  );

  // Update search text and trigger debounced fetch
  const handleSearchTextChange = (e) => {
    setSearchText(e.target.value);
    debouncedFetchData(e.target.value);
  };

  const handleOpenCreateCourseModal = () => {
    dispatch(courseActions.toggleCreateModal(true));
  };

  // Load data on component mount
  useEffect(() => {
    fetchData({});
  }, [fetchData]);

  return (
    <Box ml={{ base: 0, md: 20 }} minH="100vh" p="10">
      <CreateCourseMd />

      <Flex my={4} align="center">
        <Text flex={1} fontSize="2xl">
          Courses
        </Text>
        <Button
          colorScheme="red"
          leftIcon={<AddIcon />}
          onClick={() => handleOpenCreateCourseModal()}
        >
          Create
        </Button>
      </Flex>

      <Box w="100%" p={4} color="gray.500" borderWidth="1px" borderRadius="lg">
        <Flex m={2} justify={{ base: 'center', md: 'end' }}>
          <IconButton
            aria-label="Reload table"
            variant="link"
            onClick={fetchData}
            icon={<RepeatIcon />}
          />
          <InputGroup width={{ base: '100%', md: '350px' }}>
            <InputRightElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputRightElement>
            <Input
              name="query"
              placeholder="Search"
              value={searchText}
              onChange={handleSearchTextChange}
            />
          </InputGroup>
        </Flex>

        <Divider mt={4} />

        <DataTable
          theme={useColorModeValue('default', 'dark')}
          progressPending={status === ReducerStatus.LOADING}
          columns={courseColumns}
          data={courses}
          paginationTotalRows={metaData?.totalItems}
          onChangeRowsPerPage={handlePerRowsChange}
          onSort={handleSort}
          onChangePage={handlePageChange}
          pagination
          paginationServer
          sortServer
          progressComponent={
            <Center my={10}>
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="blue.500"
                size="xl"
              />
            </Center>
          }
        />
      </Box>
    </Box>
  );
}

export default CourseListPage;
